<template>
  <div class="operation-area">
    <!-- 信息展示区域 -->
    <div class="info-section">
      <!-- 左侧信息 -->
      <div class="left-info">
        <div class="info-item">
          <span class="label">状态</span>
          <span class="value status-badge" :class="getStatusClass(consultantInfo.status)">
            {{ userStatus }}
          </span>
        </div>

        <div class="info-item">
          <span class="label">审核记录</span>
          <el-button type="primary" size="small" @click="openAuditRecord" class="view-btn">
            查看
          </el-button>
        </div>

        <div class="info-item" v-if="consultantInfo.isAbilityAssessed">
          <span class="label">能力评定</span>
          <span class="value ability-level">{{ getAbilityLevelText(consultantInfo.abilityLevel) }}</span>
        </div>
      </div>

      <!-- 右侧信息 -->
      <div class="right-info">
        <div class="info-item">
          <span class="label">订单量</span>
          <span class="value">{{ consultantInfo.saleNum || 0 }}</span>
        </div>

        <div class="info-item">
          <span class="label">成交额</span>
          <span class="value amount">¥{{ formatAmount(consultantInfo.totalAmount) }}</span>
        </div>

        <div class="info-item">
          <span class="label">成单记录</span>
          <el-button type="text" size="small" @click="viewOrderRecord" class="view-btn">
            查看
          </el-button>
        </div>
      </div>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-buttons">
      <el-button v-if="isOperator" type="primary" size="small" @click="openRecommendSettings" class="action-btn">
        推荐设置
      </el-button>

      <el-button type="primary" size="small" @click="openModifyInfo" class="action-btn">
        修改信息
      </el-button>

      <el-button v-if="canShowRestButton" type="primary" size="small" @click="handleRest" class="action-btn">
        休息
      </el-button>

      <el-button v-if="canShowEnableButton" type="primary" size="small" @click="handleEnable" class="action-btn">
        开启
      </el-button>

      <el-button type="primary" size="small" @click="openAdjustPrice" class="action-btn">
        调整价格
      </el-button>

      <el-button
        v-if="isOperator && canShowOnlineButton"
        type="primary"
        size="small"
        @click="handleOnline"
        class="action-btn"
      >
        上线
      </el-button>

      <el-button
        v-if="isOperator && canShowOfflineButton"
        type="primary"
        size="small"
        @click="handleOffline"
        class="action-btn"
      >
        下线
      </el-button>
    </div>

    <!-- 推荐设置弹窗 -->
    <recommendSettingsDialog
      :visible.sync="showRecommendDialog"
      :consultantId="consultantId"
      :currentLevel="consultantInfo.platformRecommendRank || 0"
      @saveSuccess="handleRecommendSaveSuccess"
    />

    <!-- 修改信息弹窗 -->
    <modifyInfoDialog
      :visible.sync="showModifyInfoDialog"
      :consultantId="consultantId"
      @submitSuccess="handleModifyInfoSuccess"
    />

    <!-- 调整价格弹窗 -->
    <adjustPriceDialog
      :visible.sync="showAdjustPriceDialog"
      :consultantId="consultantId"
      :consultantInfo="consultantInfo"
      @adjustSuccess="handleAdjustPriceSuccess"
    />

    <!-- 审核记录弹窗 -->
    <auditRecordDialog :visible.sync="showAuditRecordDialog" :consultantId="consultantId" />

    <!-- 下线弹窗 -->
    <offlineDialog
      :visible.sync="showOfflineDialog"
      :consultantId="consultantId"
      @offline-success="handleOfflineSuccess"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {updateConsultantStatus, openConsultant, restConsultant} from '@/api/consultingService'
import recommendSettingsDialog from './recommendSettingsDialog.vue'
import modifyInfoDialog from './modifyInfoDialog.vue'
import adjustPriceDialog from './adjustPriceDialog.vue'
import auditRecordDialog from './auditRecordDialog.vue'
import offlineDialog from './offlineDialog.vue'

export default {
  name: 'OperationArea',
  components: {
    recommendSettingsDialog,
    modifyInfoDialog,
    adjustPriceDialog,
    auditRecordDialog,
    offlineDialog
  },
  props: {
    consultantInfo: {
      type: Object,
      default: () => ({})
    },
    consultantId: {
      type: Number,
      required: true
    },
    userStatus: {
      type: String,
      required: true,
      default: ''
    }
  },
  data() {
    return {
      showRecommendDialog: false,
      showModifyInfoDialog: false,
      showAdjustPriceDialog: false,
      showAuditRecordDialog: false,
      showOfflineDialog: false
    }
  },
  computed: {
    ...mapState(['UserInfo']),

    // 是否可见（承约人和运营人员可见）
    isVisible() {
      return this.isConsultant || this.isOperator
    },

    // 是否为承约人本人
    isConsultant() {
      return this.consultantInfo.currentConsultantUserFlag === 1
    },

    // 是否为运营人员
    isOperator() {
      return this.consultantInfo.currentManagerFlag === 1
    },

    // 是否可以显示休息按钮（仅已上线状态显示）
    canShowRestButton() {
      return this.consultantInfo.status === 1 // 已上线状态
    },

    // 是否可以显示开启按钮（休息中或已下线状态显示）
    canShowEnableButton() {
      return this.consultantInfo.status === 0 || this.consultantInfo.status === 2 // 休息中或已下线状态
    },

    // 是否可以显示上线按钮（仅已下线状态显示，且需要运营人员权限）
    canShowOnlineButton() {
      return this.consultantInfo.status === 2 // 已下线状态
    },

    // 是否可以显示下线按钮（已上线或休息中状态显示，且需要运营人员权限）
    canShowOfflineButton() {
      return this.consultantInfo.status === 1 || this.consultantInfo.status === 0 // 已上线或休息中状态
    }
  },
  created() {
    // 订单统计数据来自 consultantInfo，无需单独请求
  },
  methods: {
    // 打开审核记录弹窗
    openAuditRecord() {
      this.showAuditRecordDialog = true
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        0: '休息中',
        1: '已上线',
        2: '已下线'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态样式类
    getStatusClass(status) {
      const classMap = {
        0: 'rest',
        1: 'online',
        2: 'offline'
      }
      return classMap[status] || ''
    },

    // 获取能力等级文本
    getAbilityLevelText(level) {
      const levelMap = {
        '-3': '很低',
        '-2': '较低',
        '-1': '稍低',
        '0': '中等',
        '1': '良好',
        '2': '优秀',
        '3': '卓越'
      }
      return levelMap[level] || '未评定'
    },

    // 格式化金额
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString()
    },

    // 查看成单记录
    viewOrderRecord() {
      this.$router.push({
        path: `/saleRecord?productId=${this.consultantId}&productName=${this.consultantInfo.nickName}&business=咨询`
      })
    },

    // 打开推荐设置弹窗
    openRecommendSettings() {
      // 只有运营人员可操作
      if (this.consultantInfo.currentManagerFlag !== 1) {
        this.$message.warning('只有运营人员可以设置推荐级别')
        return
      }
      this.showRecommendDialog = true
    },

    // 打开修改信息弹窗
    async openModifyInfo() {
      // 检查是否有审核中的申请
      if (this.consultantInfo.currentUpdateApplication === 1) {
        this.$message.warning('已有在审核中的申请')
        return
      }
      this.showModifyInfoDialog = true
    },

    // 打开调整价格弹窗
    openAdjustPrice() {
      this.showAdjustPriceDialog = true
    },

    // 处理休息操作
    async handleRest() {
      if (this.consultantInfo.status !== 1) {
        this.$message.error('状态错误')
        return
      }

      try {
        await this.$confirm('休息后，别人在列表页将看不到你的可预约信息，确定吗？', '确认休息', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await restConsultant({
          consultantId: this.consultantId
        })

        this.$message.success('状态已切换为休息中')
        this.$emit('status-changed')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('休息操作失败:', error)
          this.$message.error(error.message || '操作失败')
        }
      }
    },

    // 处理开启操作
    async handleEnable() {
      try {
        // 开启前进行确认
        await this.$confirm('开启后，将可正常接受预约，确定吗？', '确认开启', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 调用开启接口，使用接口返回的提示信息
        const result = await openConsultant({consultantId: this.consultantId})

        if (result.code === 200) {
          // 成功开启
          this.$message.success('状态已切换为已上线')
          this.$emit('status-changed')
        } else {
          // 开启失败，显示接口返回的错误信息
          this.$message.warning(result.msg || '开启失败')
        }
      } catch (error) {
        // 取消操作不提示错误
        if (error !== 'cancel') {
          console.error('开启操作失败:', error)
          this.$message.error(error.message || '操作失败')
        }
      }
    },

    // 处理上线操作
    async handleOnline() {
      try {
        await this.$confirm('确认上线吗？', '确认上线', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await updateConsultantStatus({
          consultantId: this.consultantId
        })

        this.$message.success('上线成功')
        this.$emit('status-changed')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('上线操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },

    // 处理下线操作
    handleOffline() {
      this.showOfflineDialog = true
    },

    // 处理推荐设置成功
    handleRecommendSaveSuccess() {
      // 可以在这里触发父组件刷新数据
      this.$emit('status-changed')
    },

    // 处理修改信息成功
    handleModifyInfoSuccess() {
      // 可以在这里触发父组件刷新数据
      this.$emit('status-changed')
    },

    // 处理调整价格成功
    handleAdjustPriceSuccess() {
      this.$message.success('价格调整成功')
      // 可以在这里触发父组件刷新数据
      this.$emit('status-changed')
    },

    // 处理下线成功
    handleOfflineSuccess() {
      this.$emit('status-changed')
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .el-button {
  border: unset;
}
.operation-area {
  background: #fff;
  border-radius: 8px;
  padding: 35px 35px 52px 32px;
  margin-top: 32px;

  .info-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;

    .left-info,
    .right-info {
      flex: 1;

      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 32px;

        .label {
          min-width: 100px;
          font-size: 22px;
          color: #6d6d6d;
          margin-right: 80px;
        }

        .value {
          color: #333;
          font-size: 20px;
          font-weight: bold;
          &.status-badge {
            padding: 0px 12px;
            border-radius: 12px;
            font-size: 20px;
            font-weight: 500;
            width: 100px;
            text-align: center;
            line-height: 36px;
            height: 36px;
            border-radius: 57px 57px 57px 57px;
            border: 1px solid #e6e6e6;
            box-sizing: border-box;

            &.online {
              background: #e8f5e8;
              color: #52c41a;
            }

            &.rest {
              background: #fff7e6;
              
            }

            &.offline {
              background: #f5f5f5;
              color: #999;
            }
          }

          &.ability-level {
            font-weight: bold;
            color: #333;
            font-size: 20px;
          }

          &.amount {
            color: #FF724C;
              font-weight: 600;
              font-size: 20px;
          }
        }

        .view-btn {
          width: 80px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 57px 57px 57px 57px;
          border: 1px solid #5ad3cd;
          color: #5ad3cd;
          background: #fff;
          font-size: 20px;
          box-sizing: border-box;
          //   color: #1890ff;
          //   padding: 0;
          //   font-size: 14px;

          //   &:hover {
          //     color: #40a9ff;
          //   }
        }
      }
    }
  }

  .action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .action-btn {
      min-width: 80px;
      font-size: 18px;
      width: 109px;
      height: 35px;
      background: #5ad3cd;
      border-radius: 60px 60px 60px 60px;
    }
  }
}
</style>
